'use client'

import { z } from 'zod'
import { walletSchema } from '@/features/veluvit/schemas/wallet.schema'

import { publicApiHost } from '@/core/config/env'
import { ApiError, api } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import {
  paymentModelSchema,
  paymentCheckoutSchema,
  paymentOrderDetailSchema,
  paymentOrderSchema,
  paymentProductsSchema,
  purchaseAttemptSchema,
  refundAccountSchema,
  refundAttemptSchema,
  refundSchema,
  paymentAuthorizationSchema,
  tossBrandpayCheckoutEnvelopeSchema,
  tossCheckoutEnvelopeSchema,
  tossStandardOptionIdSchema,
  tossStandardPaymentMethods,
  type PaymentCheckout,
  type PaymentModel,
  type PaymentOrder,
  type PaymentProducts,
  type PaymentRefund,
  type PurchaseAttempt,
  type RefundAccount,
} from '@/features/veluvit/schemas/payment.schema'
import type { Wallet } from '@/features/veluvit/types/api.types'

const PURCHASE_KEY = 'veluvit:payment:purchase'
const REFUND_KEY = 'veluvit:payment:refund'
const RATE_KEY = 'veluvit:payment:rate'
const flights = new Map<string, Promise<PaymentOrder>>()
const refundFlights = new Map<string, Promise<PaymentRefund>>()
const orderIdSchema = z.string().min(1)

function invalid(message: string, code: string): never {
  throw new ApiError(message, 502, code)
}
function invalidMutation(message: string, code: string): never {
  const error = new ApiError(message, 502, code)
  error.unknownOutcome = true
  throw error
}
function ensureOwner(ownerId: string, identity?: number) {
  const state = useSessionStore.getState()
  if (
    !ownerId ||
    state.data?.user?.id !== ownerId ||
    state.data?.user?.guest !== false ||
    (identity !== undefined && state.identity !== identity)
  ) {
    throw new ApiError(
      '계정이 바뀌어 결제 작업을 종료했어요.',
      0,
      'PAYMENT_OWNER_CHANGED'
    )
  }
}
function scope(ownerId: string) {
  ensureOwner(ownerId)
  return useSessionStore.getState().identity
}
function valid<T>(schema: z.ZodType<T>, value: unknown, code: string): T {
  const parsed = schema.safeParse(value)
  if (!parsed.success)
    invalid(
      '결제 응답을 확인하지 못했어요. 주문 내역을 다시 확인해 주세요.',
      code
    )
  return parsed.data
}
function validMutation<T>(
  schema: z.ZodType<T>,
  value: unknown,
  code: string
): T {
  try {
    return valid(schema, value, code)
  } catch (error) {
    if (error instanceof ApiError) error.unknownOutcome = true
    throw error
  }
}
function storageGet(key: string): unknown {
  let raw: string | null
  try {
    raw = sessionStorage.getItem(key)
  } catch {
    throw new ApiError(
      '저장된 요청을 읽을 수 없어 새 요청을 시작하지 않았어요.',
      0,
      'PAYMENT_STORAGE_UNAVAILABLE'
    )
  }
  if (raw === null) return null
  try {
    return JSON.parse(raw)
  } catch {
    throw new ApiError(
      '저장된 요청을 확인할 수 없어 새 요청을 시작하지 않았어요.',
      0,
      'INVALID_PAYMENT_STORAGE'
    )
  }
}
function storageSet(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    throw new ApiError(
      '이 브라우저에 요청 번호를 보관할 수 없어 결제를 시작하지 않았어요.',
      0,
      'PAYMENT_STORAGE_UNAVAILABLE'
    )
  }
}
function storageRemove(key: string) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    return
  }
}
function purchaseKey(ownerId: string) {
  return `${PURCHASE_KEY}:${ownerId}`
}
function refundKey(ownerId: string, orderId: string) {
  return `${REFUND_KEY}:${ownerId}:${orderId}`
}
function activeRefundAttempt(key: string, requestId: string) {
  try {
    const parsed = refundAttemptSchema.safeParse(storageGet(key))
    return parsed.success && parsed.data.requestId === requestId
  } catch {
    return false
  }
}
function encoded(id: string) {
  return encodeURIComponent(orderIdSchema.parse(id))
}
function reserveProviderCall(ownerId: string) {
  const key = `${RATE_KEY}:${ownerId}`
  const now = Date.now()
  const raw = storageGet(key)
  const last = z.number().int().nonnegative().safeParse(raw)
  if (last.success && Math.abs(now - last.data) < 7000)
    throw new ApiError(
      '결제 상태를 잠시 후 다시 확인해 주세요.',
      429,
      'PAYMENT_RATE_COOLDOWN'
    )
  storageSet(key, now)
}

export async function readPaymentProducts(
  signal?: AbortSignal
): Promise<PaymentProducts> {
  return valid(
    paymentProductsSchema,
    await api<unknown>('/payment/product/', undefined, 'GET', signal),
    'INVALID_PAYMENT_PRODUCTS'
  )
}
export async function readPaymentOrders(
  ownerId: string,
  signal?: AbortSignal
): Promise<PaymentOrder[]> {
  const identity = scope(ownerId)
  const envelope = valid(
    z.union([z.array(z.unknown()), z.object({ orders: z.array(z.unknown()) })]),
    await api<unknown>('/payment/order/', undefined, 'GET', signal),
    'INVALID_PAYMENT_ORDERS'
  )
  ensureOwner(ownerId, identity)
  const rows = Array.isArray(envelope) ? envelope : envelope.orders
  return rows.flatMap((row) => {
    const order = paymentOrderSchema.safeParse(row)
    if (order.success) return [order.data]
    if (
      z
        .looseObject({ id: z.string().min(1), provider: z.literal('test') })
        .safeParse(row).success
    )
      return []
    return invalid(
      '결제 내역 일부를 확인하지 못했어요.',
      'INVALID_PAYMENT_ORDERS'
    )
  })
}
export async function readPaymentOrder(
  ownerId: string,
  orderId: string,
  signal?: AbortSignal
): Promise<PaymentOrder> {
  const identity = scope(ownerId)
  const result = valid(
    paymentOrderDetailSchema,
    await api<unknown>(
      `/payment/order/${encoded(orderId)}/`,
      undefined,
      'GET',
      signal
    ),
    'INVALID_PAYMENT_ORDER'
  )
  ensureOwner(ownerId, identity)
  if (result.id !== orderId)
    invalid('다른 주문의 응답이 도착했어요.', 'PAYMENT_ORDER_MISMATCH')
  return result
}
export async function syncPaymentOrder(
  ownerId: string,
  orderId: string
): Promise<PaymentOrder> {
  const identity = scope(ownerId)
  reserveProviderCall(ownerId)
  const result = validMutation(
    paymentOrderSchema,
    await api<unknown>(`/payment/order/${encoded(orderId)}/sync/`, {}, 'POST'),
    'INVALID_PAYMENT_ORDER'
  )
  ensureOwner(ownerId, identity)
  if (result.id !== orderId)
    invalidMutation('다른 주문의 응답이 도착했어요.', 'PAYMENT_ORDER_MISMATCH')
  return result
}
export function readPurchaseAttempt(ownerId: string): PurchaseAttempt | null {
  ensureOwner(ownerId)
  const key = purchaseKey(ownerId)
  const raw = storageGet(key)
  if (raw === null) return null
  const parsed = purchaseAttemptSchema.safeParse(raw)
  if (!parsed.success || parsed.data.ownerId !== ownerId)
    throw new ApiError(
      '저장된 결제 요청을 확인할 수 없어요.',
      0,
      'INVALID_PURCHASE_ATTEMPT'
    )
  return parsed.data
}
export function preparePurchase(
  ownerId: string,
  productId: string,
  { fresh = false }: { fresh?: boolean } = {}
): PurchaseAttempt {
  ensureOwner(ownerId)
  const product = orderIdSchema.parse(productId)
  const existing = fresh ? null : readPurchaseAttempt(ownerId)
  if (existing) {
    if (existing.productId !== product)
      throw new ApiError(
        '진행 중인 주문을 먼저 확인해 주세요.',
        409,
        'PURCHASE_ATTEMPT_PENDING'
      )
    return existing
  }
  const attempt: PurchaseAttempt = {
    version: 1,
    ownerId,
    requestId: crypto.randomUUID(),
    productId: product,
  }
  storageSet(purchaseKey(ownerId), attempt)
  return attempt
}
export function clearPurchaseAttempt(ownerId: string) {
  ensureOwner(ownerId)
  storageRemove(purchaseKey(ownerId))
}
async function postExistingAttempt(
  ownerId: string,
  attempt: PurchaseAttempt
): Promise<PaymentOrder> {
  const identity = scope(ownerId)
  const key = `${ownerId}:${attempt.requestId}`
  const pending = flights.get(key)
  if (pending) return pending
  const flight = (async () => {
    let response: unknown
    try {
      response = await api<unknown>(
        '/payment/order/',
        {
          requestId: attempt.requestId,
          productId: attempt.productId,
          provider: 'toss',
        },
        'POST'
      )
    } catch (cause) {
      ensureOwner(ownerId, identity)
      if (
        cause instanceof ApiError &&
        cause.status >= 400 &&
        cause.status < 500 &&
        cause.status !== 409 &&
        !cause.unknownOutcome
      ) {
        const current = readPurchaseAttempt(ownerId)
        if (current?.requestId === attempt.requestId && !current.orderId)
          storageRemove(purchaseKey(ownerId))
      }
      throw cause
    }
    ensureOwner(ownerId, identity)
    const parsed = paymentOrderSchema.safeParse(response)
    if (!parsed.success)
      invalidMutation(
        '결제창을 열지 못했어요. 다시 시도해 주세요.',
        'INVALID_PAYMENT_ORDER'
      )
    const order = parsed.data
    if (order.provider !== 'toss')
      invalidMutation(
        '새 결제 서비스를 아직 연결하지 못했어요. 잠시 후 다시 시도해 주세요.',
        'PAYMENT_PROVIDER_MISMATCH'
      )
    if (order.product.id !== attempt.productId)
      invalidMutation(
        '선택한 상품과 다른 주문이 도착했어요. 상품을 다시 선택해 주세요.',
        'PAYMENT_PRODUCT_MISMATCH'
      )
    const current = readPurchaseAttempt(ownerId)
    if (
      !current ||
      current.requestId !== attempt.requestId ||
      current.productId !== attempt.productId
    )
      throw new ApiError(
        '결제 요청이 변경되어 응답을 버렸어요.',
        0,
        'PURCHASE_ATTEMPT_CHANGED'
      )
    try {
      storageSet(purchaseKey(ownerId), { ...current, orderId: order.id })
    } catch (error) {
      if (error instanceof ApiError) error.unknownOutcome = true
      throw error
    }
    return order
  })().finally(() => {
    if (flights.get(key) === flight) flights.delete(key)
  })
  flights.set(key, flight)
  return flight
}
export async function createPaymentOrder(
  ownerId: string,
  productId: string
): Promise<PaymentOrder> {
  const attempt = readPurchaseAttempt(ownerId)
  if (!attempt || attempt.productId !== productId)
    throw new ApiError(
      '저장된 구매 요청이 없어요. 상품을 다시 선택해 주세요.',
      409,
      'PURCHASE_ATTEMPT_REQUIRED'
    )
  if (attempt.orderId) return readPaymentOrder(ownerId, attempt.orderId)
  return postExistingAttempt(ownerId, attempt)
}
export function checkedTossCallback(value: string) {
  const url = new URL(value)
  const allowed =
    url.origin === window.location.origin ||
    (url.protocol === 'https:' && !!publicApiHost && url.host === publicApiHost)
  if (
    !allowed ||
    url.username ||
    url.password ||
    url.hash ||
    !url.pathname.startsWith('/api/integration/toss/')
  )
    invalid('결제 복귀 주소를 확인할 수 없어요.', 'INVALID_PAYMENT_RETURN_URL')
  return value
}
export function validateTossCheckout(
  value: unknown,
  order: PaymentOrder,
  optionId: string,
  model: PaymentModel
): PaymentCheckout {
  let checkout = value
  if (typeof value === 'object' && value !== null && 'checkout' in value) {
    if (optionId === 'brandpay') {
      const envelope = valid(
        tossBrandpayCheckoutEnvelopeSchema,
        value,
        'INVALID_PAYMENT_CHECKOUT'
      )
      const { request, clientKey, customerKey, redirectUrl } = envelope.checkout
      if (envelope.orderId !== request.orderId)
        invalid('결제 요청 정보가 주문과 달라요.', 'PAYMENT_CHECKOUT_MISMATCH')
      checkout = {
        ...request,
        amount: request.amount.value,
        currency: request.amount.currency,
        clientKey,
        customerKey,
        brandpayRedirectUrl: redirectUrl,
        paymentOption: {
          id: 'brandpay',
          label: '등록한 카드로 결제',
          kind: 'brandpay',
          available: true,
          models: ['WEB', 'MOB'],
        },
      }
    } else {
      const envelope = valid(
        tossCheckoutEnvelopeSchema,
        value,
        'INVALID_PAYMENT_CHECKOUT'
      )
      const option = valid(
        tossStandardOptionIdSchema,
        optionId,
        'PAYMENT_OPTION_UNAVAILABLE'
      )
      const { request, clientKey, customerKey } = envelope.checkout
      if (
        envelope.orderId !== request.orderId ||
        request.method !== tossStandardPaymentMethods[option]
      )
        invalid('결제 요청 정보가 주문과 달라요.', 'PAYMENT_CHECKOUT_MISMATCH')
      const labels = {
        card: '카드·간편결제',
        bank: '계좌이체',
        vacnt: '가상계좌',
        phone: '휴대폰',
      }
      checkout = {
        ...request,
        amount: request.amount.value,
        currency: request.amount.currency,
        clientKey,
        customerKey,
        paymentOption: {
          id: option,
          label: labels[option],
          kind: 'payment',
          method: request.method,
          available: true,
          models: ['WEB', 'MOB'],
        },
      }
    }
  }
  const result = valid(
    paymentCheckoutSchema,
    checkout,
    'INVALID_PAYMENT_CHECKOUT'
  )
  if (
    order.provider !== 'toss' ||
    order.isTest !== order.payment.sandbox ||
    result.clientKey.startsWith('test_') !== order.isTest
  )
    invalid(
      '주문의 결제 환경을 확인할 수 없어요.',
      'PAYMENT_ORDER_ENV_MISMATCH'
    )
  if (
    result.orderId !== order.id ||
    result.amount !== order.product.amount ||
    result.paymentOption.id !== optionId
  )
    invalid('결제 요청 정보가 주문과 달라요.', 'PAYMENT_CHECKOUT_MISMATCH')
  if (
    !result.paymentOption.available ||
    !result.paymentOption.models.includes(model)
  )
    invalid(
      '현재 환경에서 사용할 수 없는 결제수단이에요.',
      'PAYMENT_OPTION_UNAVAILABLE'
    )
  if (result.successUrl) checkedTossCallback(result.successUrl)
  if (result.failUrl) checkedTossCallback(result.failUrl)
  if (result.brandpayRedirectUrl)
    checkedTossCallback(result.brandpayRedirectUrl)
  return result
}
export async function startPaymentCheckout(
  ownerId: string,
  order: PaymentOrder,
  optionId: string,
  model: PaymentModel,
  paymentMethodId?: string
): Promise<PaymentCheckout> {
  const identity = scope(ownerId)
  const id = valid(paymentOrderSchema, order, 'INVALID_PAYMENT_ORDER').id
  if (order.provider !== 'toss')
    invalid(
      '이전 결제 서비스의 주문은 다시 결제할 수 없어요.',
      'PAYMENT_PROVIDER_MISMATCH'
    )
  const parsedModel = paymentModelSchema.parse(model)
  const parsedOptionId = orderIdSchema.parse(optionId)
  reserveProviderCall(ownerId)
  const response = await api<unknown>(
    `/payment/order/${encoded(id)}/checkout/`,
    {
      model: parsedModel,
      paymentOptionId: parsedOptionId,
      returnOrigin: window.location.origin,
      ...(paymentMethodId
        ? { paymentMethodId: orderIdSchema.parse(paymentMethodId) }
        : {}),
    },
    'POST'
  )
  ensureOwner(ownerId, identity)
  try {
    const checkout = validateTossCheckout(
      response,
      order,
      parsedOptionId,
      parsedModel
    )
    if (
      paymentMethodId &&
      (checkout.paymentOption.kind !== 'brandpay' ||
        checkout.methodId !== paymentMethodId)
    )
      invalidMutation(
        '선택한 카드와 결제 요청이 달라요.',
        'PAYMENT_METHOD_MISMATCH'
      )
    return checkout
  } catch (error) {
    if (error instanceof ApiError) error.unknownOutcome = true
    throw error
  }
}
/** SDK 인증 결과는 성공이 아니다. 서버 승인·증빙 검증 결과만 사용한다. */
export async function confirmPaymentOrder(
  ownerId: string,
  order: PaymentOrder,
  authorization: unknown
): Promise<PaymentOrder> {
  const identity = scope(ownerId)
  const proof = valid(
    paymentAuthorizationSchema,
    authorization,
    'INVALID_PAYMENT_AUTHORIZATION'
  )
  if (
    order.provider !== 'toss' ||
    proof.orderId !== order.id ||
    proof.amount.value !== order.product.amount
  )
    invalid('인증 결과가 주문과 달라요.', 'PAYMENT_AUTHORIZATION_MISMATCH')
  const response = await api<unknown>(
    `/payment/order/${encoded(order.id)}/confirm/`,
    { paymentKey: proof.paymentKey, amount: proof.amount.value },
    'POST'
  )
  ensureOwner(ownerId, identity)
  const result = validMutation(
    paymentOrderSchema,
    response,
    'INVALID_PAYMENT_ORDER'
  )
  if (
    result.id !== order.id ||
    result.provider !== 'toss' ||
    result.product.id !== order.product.id ||
    result.product.amount !== order.product.amount ||
    result.isTest !== order.isTest ||
    result.payment.sandbox !== order.payment.sandbox
  )
    invalidMutation(
      '다른 주문의 승인 응답이 도착했어요.',
      'PAYMENT_ORDER_MISMATCH'
    )
  return result
}

async function fingerprint(
  amount: number,
  reason: string,
  method: string,
  account?: RefundAccount
) {
  const bytes = new TextEncoder().encode(
    JSON.stringify([amount, reason, method, account ?? null])
  )
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('')
}
export async function requestPaymentRefund(
  ownerId: string,
  order: PaymentOrder,
  amount: number,
  reason: string,
  refundAccount?: RefundAccount
): Promise<PaymentRefund> {
  const identity = scope(ownerId)
  const verified = valid(paymentOrderSchema, order, 'INVALID_PAYMENT_ORDER')
  const method = verified.payment.payMethod
  if (!method)
    throw new ApiError(
      '결제 수단을 확인할 수 없어 환불을 요청하지 않았어요.',
      409,
      'REFUND_METHOD_UNKNOWN'
    )
  if (verified.status !== 'paid' && verified.status !== 'partially_refunded')
    throw new ApiError(
      '환불 가능한 결제 상태가 아니에요.',
      409,
      'REFUND_STATUS_INVALID'
    )
  if (
    !Number.isSafeInteger(amount) ||
    amount <= 0 ||
    amount > verified.product.amount - verified.refundedAmount ||
    !reason.trim()
  )
    throw new ApiError(
      '환불 금액과 사유를 확인해 주세요.',
      400,
      'INVALID_REFUND_REQUEST'
    )
  const needsAccount = [
    'BANK',
    'VACNT',
    'TRANSFER',
    'VIRTUAL_ACCOUNT',
  ].includes(method)
  if (needsAccount && !refundAccount)
    throw new ApiError('환불 계좌가 필요해요.', 400, 'REFUND_ACCOUNT_REQUIRED')
  const account = needsAccount
    ? refundAccountSchema.parse(refundAccount)
    : undefined
  const key = refundKey(ownerId, verified.id)
  const bodyFingerprint = await fingerprint(
    amount,
    reason.trim(),
    method,
    account
  )
  ensureOwner(ownerId, identity)
  const existingRaw = storageGet(key)
  const existing =
    existingRaw === null
      ? null
      : valid(refundAttemptSchema, existingRaw, 'INVALID_REFUND_ATTEMPT')
  if (
    existing &&
    (existing.ownerId !== ownerId ||
      existing.orderId !== verified.id ||
      existing.fingerprint !== bodyFingerprint)
  )
    throw new ApiError(
      '이전 환불 요청의 처리 결과를 먼저 확인해 주세요.',
      409,
      'REFUND_ATTEMPT_PENDING'
    )
  const requestId = existing?.requestId ?? crypto.randomUUID()
  if (!existing)
    storageSet(key, {
      version: 1,
      ownerId,
      orderId: verified.id,
      requestId,
      fingerprint: bodyFingerprint,
    })
  const pending = refundFlights.get(key)
  if (pending) return pending
  const flight = (async () => {
    let raw: unknown
    try {
      raw = await api<unknown>(
        `/payment/order/${encoded(verified.id)}/refund/`,
        {
          requestId,
          amount,
          reason: reason.trim(),
          ...(account ? { refundAccount: account } : {}),
        },
        'POST'
      )
    } catch (cause) {
      if (
        cause instanceof ApiError &&
        cause.status >= 400 &&
        cause.status < 500 &&
        cause.status !== 409 &&
        !cause.unknownOutcome &&
        activeRefundAttempt(key, requestId)
      )
        storageRemove(key)
      throw cause
    }
    const response = validMutation(refundSchema, raw, 'INVALID_PAYMENT_REFUND')
    ensureOwner(ownerId, identity)
    if (response.orderId !== verified.id)
      invalidMutation(
        '다른 주문의 환불 응답이 도착했어요.',
        'PAYMENT_ORDER_MISMATCH'
      )
    storageRemove(key)
    return response
  })().finally(() => {
    if (refundFlights.get(key) === flight) refundFlights.delete(key)
  })
  refundFlights.set(key, flight)
  return flight
}
export async function refreshPaymentWallet(ownerId: string): Promise<Wallet> {
  const identity = scope(ownerId)
  const wallet = valid(
    walletSchema,
    await api<unknown>('/wallet/'),
    'INVALID_PAYMENT_WALLET'
  )
  ensureOwner(ownerId, identity)
  useSessionStore.getState().applyWallet(wallet)
  return wallet
}
export type { PaymentOrder }
